import { useEffect, useRef, type KeyboardEvent } from "react";

import { useGlide } from "@/lib/useGlide";

import segmented from "../Segmented.module.css";
import styles from "./PresetChoice.module.css";

/**
 * A handful of common millimetre figures as one row of segments, plus
 * „Eigenes Maß" — the one-click way to a figure that comes up again and
 * again („ein 150er Fallrohr"), without taking the typed way away (A5,
 * 23.09.2026).
 *
 * A radio group, and steered like one: the checked segment is the tab stop,
 * the arrow keys move and pick, and the digits 1…n pick the n-th figure
 * directly. „Eigenes Maß" picks nothing by itself — it hands over to the
 * field the caller shows for it (`onCustom`).
 *
 * The figures are mono with tabular digits, like every measure in this app;
 * the accessible name carries the unit („150 mm"), the segment only the
 * number, because the group's label already says what is counted.
 *
 * `autoFocus` puts the checked segment in hand on mount — the moment right
 * after a new downpipe is set, where `onConfirm` (Enter) keeps what is
 * marked.
 *
 * The marked segment is the knob that glides, as in `SegmentedControl`
 * (design 2026-10-24 §4.2): a radio group with `data-thumb` and the thumb
 * after the radios, placed by `useGlide`.
 */
export function PresetChoice({
  label,
  presets,
  value,
  customActive,
  onPick,
  onCustom,
  onConfirm,
  autoFocus = false,
}: {
  /** The group's name — „Rohr", „Größe". */
  label: string;
  presets: readonly number[];
  /** The figure standing now, or `null` where none can be read. */
  value: number | null;
  /** Whether „Eigenes Maß" is the marked segment — also when the standing
   * figure is simply none of the presets. */
  customActive: boolean;
  onPick: (mm: number) => void;
  onCustom: () => void;
  /** Enter on the group. Absent, Enter does what a button does. */
  onConfirm?: () => void;
  autoFocus?: boolean;
}) {
  const groupRef = useRef<HTMLDivElement>(null);
  const thumbRef = useRef<HTMLSpanElement>(null);
  const checkedIndex = customActive
    ? presets.length
    : presets.findIndex((each) => each === value);
  useGlide("radio", groupRef, thumbRef, presets.length + 1, String(checkedIndex));
  // Nothing marked (a figure the row does not know, before „Eigenes Maß" is
  // shown for it): the first segment is the tab stop, as the ARIA pattern
  // asks of a group with nothing checked.
  const tabStop = checkedIndex === -1 ? 0 : checkedIndex;

  // On mount only: the choice is put in hand once, not re-grabbed on every
  // figure it changes to.
  const focusOnMount = useRef(autoFocus ? tabStop : null);
  useEffect(() => {
    const index = focusOnMount.current;
    if (index === null) return;
    groupRef.current?.querySelectorAll<HTMLButtonElement>("button")[index]?.focus();
  }, []);

  /** The segments stay mounted across a pick, so the one that is about to be
   * checked can take the focus right away. */
  function focusSegment(index: number): void {
    groupRef.current?.querySelectorAll<HTMLButtonElement>("button")[index]?.focus();
  }

  function pick(index: number): void {
    if (index === presets.length) {
      onCustom();
      return;
    }
    const mm = presets[index];
    if (mm !== undefined) onPick(mm);
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>): void {
    const count = presets.length + 1;
    const here = Math.max(
      Array.from(groupRef.current?.querySelectorAll("button") ?? []).indexOf(
        document.activeElement as HTMLButtonElement,
      ),
      0,
    );
    if (event.key === "Enter" && onConfirm) {
      event.preventDefault();
      onConfirm();
      return;
    }
    const digit = Number(event.key);
    if (Number.isInteger(digit) && digit >= 1 && digit <= presets.length) {
      event.preventDefault();
      pick(digit - 1);
      focusSegment(digit - 1);
      return;
    }
    const step =
      event.key === "ArrowRight" || event.key === "ArrowDown"
        ? 1
        : event.key === "ArrowLeft" || event.key === "ArrowUp"
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (here + step + count) % count;
    pick(next);
    // „Eigenes Maß" hands the focus to its field; everything else keeps it
    // on the row.
    if (next !== presets.length) focusSegment(next);
  }

  return (
    <div
      ref={groupRef}
      className={segmented.group}
      role="radiogroup"
      aria-label={label}
      data-thumb=""
      onKeyDown={onKeyDown}
    >
      {presets.map((mm, index) => (
        <button
          key={mm}
          type="button"
          role="radio"
          aria-checked={index === checkedIndex}
          aria-label={`${mm} mm`}
          tabIndex={index === tabStop ? 0 : -1}
          className={`${segmented.segment} ${styles.figure}`}
          onClick={() => pick(index)}
        >
          {mm}
        </button>
      ))}
      <button
        type="button"
        role="radio"
        aria-checked={customActive}
        tabIndex={tabStop === presets.length ? 0 : -1}
        className={segmented.segment}
        onClick={() => pick(presets.length)}
      >
        Eigenes Maß
      </button>
      {/* After the radios: the first radio stays the group's first child, and
          the keys above count buttons only. */}
      <span ref={thumbRef} className={segmented.thumb} aria-hidden="true" />
    </div>
  );
}
